import { useState, useEffect, useCallback, useMemo, useRef } from 'react';

const EMPTY_TASK_COMPOSER_FORM = { name: '', owner_id: '' };
const EMPTY_OBJECTIVE_COMPOSER_FORM = { name: '', project_id: '', owner_id: '', effort: '1', description: '' };

const normalizeObjectiveEffort = (value, fallback = '1') => {
  const parsed = Number.parseInt(value, 10);
  if (Number.isInteger(parsed) && parsed >= 0) return String(parsed);
  const fallbackParsed = Number.parseInt(fallback, 10);
  if (Number.isInteger(fallbackParsed) && fallbackParsed >= 0) return String(fallbackParsed);
  return '1';
};

const buildEmptyPointComposer = (projectId = null) => ({
  mode: null,
  point_id: null,
  task_form: { ...EMPTY_TASK_COMPOSER_FORM },
  objective_form: {
    ...EMPTY_OBJECTIVE_COMPOSER_FORM,
    project_id: projectId ? String(projectId) : '',
  },
});

const buildMeetingSignature = (meetings) => {
  if (!Array.isArray(meetings)) return '';
  return meetings
    .map((meeting) =>
      [
        meeting?.id,
        meeting?.circle_id,
        meeting?.status,
        meeting?.created_by,
        meeting?.updated_at,
        meeting?.started_at,
      ]
        .map((value) => String(value ?? ''))
        .join(':')
    )
    .sort()
    .join('|');
};

const normalizeProjectReviewContext = (entry) => {
  if (!entry || typeof entry !== 'object') return null;
  const rawProjectId = entry.project_id ?? entry.current?.project_id ?? entry.last?.project_id;
  if (rawProjectId === undefined || rawProjectId === null || rawProjectId === '') return null;
  return {
    project_id: Number(rawProjectId),
    current: entry.current || null,
    last: entry.last || null,
  };
};

const mergeProjectReviewContext = (items, review) => {
  if (!review || review.project_id === undefined || review.project_id === null) return items;
  const key = String(review.project_id);
  const nextItems = Array.isArray(items) ? [...items] : [];
  const index = nextItems.findIndex((item) => String(item?.project_id) === key);
  if (index >= 0) {
    nextItems[index] = { ...nextItems[index], current: review };
    return nextItems;
  }
  nextItems.push({ project_id: Number(review.project_id), current: review, last: null });
  return nextItems;
};

const useMeetingState = ({
  apiUrl,
  getAuthHeaders,
  user,
  selectedCircle,
  setSelectedCircle,
  activeTab,
  setActiveTab,
  circles,
  normalizeTension,
}) => {
  const [activeMeeting, setActiveMeeting] = useState(null);
  const [meetingTensions, setMeetingTensions] = useState([]);
  const [meetingParticipants, setMeetingParticipants] = useState([]);
  const [meetingNotes, setMeetingNotes] = useState('');
  const [meetingPresenceCount, setMeetingPresenceCount] = useState(0);
  const [meetingConnectedUsers, setMeetingConnectedUsers] = useState([]);
  const [meetingRealtimeConnected, setMeetingRealtimeConnected] = useState(false);
  const [meetingFormVisible, setMeetingFormVisible] = useState(false);
  const [meetingPhase, setMeetingPhase] = useState('inclusion');
  const [selectedMeetingTensionIdShared, setSelectedMeetingTensionIdShared] = useState(null);
  const [meetingLoading, setMeetingLoading] = useState(false);
  const [meetingError, setMeetingError] = useState(null);
  const [meetingPoints, setMeetingPoints] = useState([]);
  const [meetingPreparedPoints, setMeetingPreparedPoints] = useState([]);
  const [meetingPreparedPointsLoading, setMeetingPreparedPointsLoading] = useState(false);
  const [meetingPreparedPointsError, setMeetingPreparedPointsError] = useState(null);
  const [selectedMeetingPointId, setSelectedMeetingPointId] = useState(null);
  const [selectedMeetingPointIdShared, setSelectedMeetingPointIdShared] = useState(null);
  const [selectedMeetingProjectId, setSelectedMeetingProjectId] = useState(null);
  const [selectedMeetingProjectIdShared, setSelectedMeetingProjectIdShared] = useState(null);
  const [meetingProjectReviews, setMeetingProjectReviews] = useState([]);
  const [meetingPointComposer, setMeetingPointComposer] = useState(buildEmptyPointComposer());
  const [openMeetings, setOpenMeetings] = useState([]);
  const [meetingHistory, setMeetingHistory] = useState([]);
  const [meetingHistoryLoading, setMeetingHistoryLoading] = useState(false);
  const [meetingHistoryError, setMeetingHistoryError] = useState(null);
  const [showMeetingHistory, setShowMeetingHistory] = useState(false);
  const [meetingDraft, setMeetingDraft] = useState({
    id: null,
    circle_id: null,
    title: '',
    summary: '',
    status: 'draft',
    expressed_by_role_id: '',
    action: 'edit',
    original_data: { name: '', purpose: '', domains: [], accountabilities: [], parent_id: null },
    proposed_data: { name: '', purpose: '', domains: [], accountabilities: [], parent_id: null },
    operations: [],
  });
  const [selectedMeetingTensionId, setSelectedMeetingTensionId] = useState(null);

  const effectiveUserId = user?.employee_id ?? user?.id;

  const meetingSocketRef = useRef(null);
  const meetingNoteDebounce = useRef(null);
  const tensionEditDebounce = useRef(null);
  const pendingTensionUpdate = useRef(null);
  const meetingRejoinPending = useRef(false);
  const meetingPointNoteDebounce = useRef(null);
  const openMeetingRequestSeq = useRef(0);
  const selectedCircleIdRef = useRef(selectedCircle?.id ?? null);
  selectedCircleIdRef.current = selectedCircle?.id ?? null;

  const fetchMeetingTensions = useCallback(async (meetingId) => {
    if (!meetingId) return;
    try {
      const response = await fetch(`${apiUrl}/circle-meetings/${meetingId}/tensions`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error("Impossible de recuperer les tensions de la réunion");
      const data = await response.json();
      const normalized = (data || []).map(normalizeTension);
      setMeetingTensions(normalized);
      const isPast = (status) => ['applied', 'rejected'].includes((status || '').toLowerCase());
      const activeTensions = normalized.filter((t) => !isPast(t.status));
      const stillSelected = selectedMeetingTensionId && activeTensions.some((t) => t.id === selectedMeetingTensionId);
      if (activeTensions.length === 0) {
        setSelectedMeetingTensionId(null);
      } else if (!stillSelected) {
        setSelectedMeetingTensionId(activeTensions[0].id);
      }
    } catch (err) {
      setMeetingError(err.message);
    }
  }, [apiUrl, getAuthHeaders, selectedMeetingTensionId]);

  const fetchMeetingParticipants = useCallback(async (meetingId) => {
    if (!meetingId) return;
    try {
      const response = await fetch(`${apiUrl}/circle-meetings/${meetingId}/participants`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error("Impossible de recuperer les participants");
      const data = await response.json();
      setMeetingParticipants(data || []);
    } catch (err) {
      setMeetingError(err.message);
    }
  }, [apiUrl, getAuthHeaders]);

  const fetchMeetingPreparedPoints = useCallback(async (meetingIdOverride) => {
    const meetingId = meetingIdOverride || activeMeeting?.id;
    if (!meetingId) {
      setMeetingPreparedPoints([]);
      return;
    }
    try {
      setMeetingPreparedPointsLoading(true);
      setMeetingPreparedPointsError(null);
      const response = await fetch(`${apiUrl}/circle-meetings/${meetingId}/prep-points`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error("Impossible de recuperer les points");
      const data = await response.json();
      setMeetingPreparedPoints(Array.isArray(data) ? data : []);
    } catch (err) {
      setMeetingPreparedPoints([]);
      setMeetingPreparedPointsError(err.message);
    } finally {
      setMeetingPreparedPointsLoading(false);
    }
  }, [activeMeeting?.id, apiUrl, getAuthHeaders]);

  const fetchOpenMeeting = useCallback(async (circleId) => {
    if (!circleId) return;
    const requestSeq = ++openMeetingRequestSeq.current;
    try {
      setMeetingLoading(true);
      const response = await fetch(`${apiUrl}/circle-meetings/open?circle_id=${circleId}`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error("Impossible de recuperer les réunions");
      const data = await response.json();
      if (requestSeq !== openMeetingRequestSeq.current) return;
      if (String(selectedCircleIdRef.current ?? '') !== String(circleId)) return;
      const meeting = data && data.length > 0 ? data[0] : null;
      setActiveMeeting(meeting);
      if (meeting) {
        fetchMeetingTensions(meeting.id);
        fetchMeetingParticipants(meeting.id);
      } else {
        setMeetingTensions([]);
        setMeetingParticipants([]);
        setMeetingPoints([]);
        setMeetingPreparedPoints([]);
        setMeetingPreparedPointsLoading(false);
        setMeetingPreparedPointsError(null);
        setSelectedMeetingPointId(null);
        setSelectedMeetingPointIdShared(null);
        setSelectedMeetingProjectId(null);
        setSelectedMeetingProjectIdShared(null);
        setMeetingProjectReviews([]);
        setMeetingPointComposer(buildEmptyPointComposer());
      }
      setMeetingError(null);
    } catch (err) {
      if (requestSeq !== openMeetingRequestSeq.current) return;
      if (String(selectedCircleIdRef.current ?? '') !== String(circleId)) return;
      setMeetingError(err.message);
    } finally {
      if (requestSeq === openMeetingRequestSeq.current) {
        setMeetingLoading(false);
      }
    }
  }, [apiUrl, getAuthHeaders, fetchMeetingParticipants, fetchMeetingTensions]);

  const fetchOpenMeetingsForUser = useCallback(async () => {
    if (!effectiveUserId) {
      setOpenMeetings([]);
      return;
    }
    try {
      const response = await fetch(`${apiUrl}/circle-meetings/open-for-user`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error("Impossible de recuperer les réunions ouvertes");
      const data = await response.json();
      const next = Array.isArray(data) ? data : [];
      setOpenMeetings((prev) => {
        const prevSig = buildMeetingSignature(prev);
        const nextSig = buildMeetingSignature(next);
        return prevSig === nextSig ? prev : next;
      });
    } catch (err) {
      setOpenMeetings([]);
    }
  }, [apiUrl, getAuthHeaders, effectiveUserId]);

  useEffect(() => {
    fetchOpenMeetingsForUser();
    const interval = setInterval(fetchOpenMeetingsForUser, 10000);
    const handleFocus = () => fetchOpenMeetingsForUser();
    window.addEventListener('focus', handleFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', handleFocus);
    };
  }, [fetchOpenMeetingsForUser]);

  const openMeetingsByCircle = useMemo(() => {
    const map = new Map();
    (openMeetings || []).forEach((meeting) => {
      if (meeting?.circle_id !== undefined && meeting?.circle_id !== null) {
        const key = String(meeting.circle_id);
        if (!map.has(key)) map.set(key, meeting);
      }
    });
    return map;
  }, [openMeetings]);

  const openMeetingCircleIds = useMemo(() => new Set(openMeetingsByCircle.keys()), [openMeetingsByCircle]);

  useEffect(() => {
    if (selectedCircle?.id) {
      fetchOpenMeeting(selectedCircle.id);
    } else {
      openMeetingRequestSeq.current += 1;
      setActiveMeeting(null);
      setMeetingTensions([]);
      setMeetingProjectReviews([]);
      setMeetingLoading(false);
    }
  }, [selectedCircle?.id, fetchOpenMeeting]);

  useEffect(() => {
    if (!activeMeeting) return;
    fetchMeetingTensions(activeMeeting.id);
    fetchMeetingParticipants(activeMeeting.id);
    const id = setInterval(() => {
      fetchMeetingTensions(activeMeeting.id);
    }, 5000);
    return () => clearInterval(id);
  }, [activeMeeting, fetchMeetingTensions, fetchMeetingParticipants]);

  useEffect(() => {
    if (!activeMeeting?.id) {
      setMeetingPreparedPoints([]);
      return;
    }
    fetchMeetingPreparedPoints(activeMeeting.id);
  }, [activeMeeting?.id, meetingConnectedUsers, fetchMeetingPreparedPoints]);

  useEffect(() => {
    if (activeTab === 'meeting' && !activeMeeting && !meetingLoading && !meetingRejoinPending.current) {
      setActiveTab('structure');
    }
  }, [activeTab, activeMeeting, meetingLoading]);

  const sendMeetingRealtime = useCallback((payload) => {
    const ws = meetingSocketRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(payload));
      return true;
    }
    return false;
  }, []);

  const normalizeMeetingDraft = useCallback((draft) => {
    const baseSnapshot = { name: '', purpose: '', domains: [], accountabilities: [], parent_id: null };
    const baseDraft = {
      id: null,
      circle_id: null,
      title: '',
      summary: '',
      status: 'draft',
      expressed_by_role_id: '',
      action: 'edit',
      original_data: baseSnapshot,
      proposed_data: baseSnapshot,
      operations: [],
    };
    if (!draft) return baseDraft;
    let operations = draft.operations || [];
    if (!Array.isArray(operations) && typeof operations === 'string') {
      try {
        operations = JSON.parse(operations);
      } catch (err) {
        operations = [];
      }
    }
    if (!Array.isArray(operations)) operations = [];
    return {
      ...baseDraft,
      ...draft,
      operations,
      original_data: draft.original_data || baseSnapshot,
      proposed_data: draft.proposed_data || baseSnapshot,
    };
  }, []);

  const normalizePointComposer = useCallback((composer, fallbackProjectId = null) => {
    const base = buildEmptyPointComposer(fallbackProjectId);
    if (!composer || typeof composer !== 'object') return base;
    const mode = composer.mode === 'task' || composer.mode === 'objective' ? composer.mode : null;
    const pointId = composer.point_id !== undefined && composer.point_id !== null && composer.point_id !== ''
      ? String(composer.point_id)
      : null;
    const taskInput = composer.task_form && typeof composer.task_form === 'object' ? composer.task_form : {};
    const objectiveInput = composer.objective_form && typeof composer.objective_form === 'object' ? composer.objective_form : {};
    return {
      mode,
      point_id: pointId,
      task_form: {
        name: taskInput.name || '',
        owner_id: taskInput.owner_id !== undefined && taskInput.owner_id !== null && taskInput.owner_id !== ''
          ? String(taskInput.owner_id)
          : '',
      },
      objective_form: {
        name: objectiveInput.name || '',
        project_id: objectiveInput.project_id !== undefined && objectiveInput.project_id !== null && objectiveInput.project_id !== ''
          ? String(objectiveInput.project_id)
          : (fallbackProjectId ? String(fallbackProjectId) : ''),
        owner_id: objectiveInput.owner_id !== undefined && objectiveInput.owner_id !== null && objectiveInput.owner_id !== ''
          ? String(objectiveInput.owner_id)
          : '',
        effort: normalizeObjectiveEffort(objectiveInput.effort, base.objective_form.effort),
        description: objectiveInput.description || '',
      },
    };
  }, []);

  useEffect(() => {
    const meetingId = activeMeeting?.id;
    const authHeaders = getAuthHeaders();
    const token = (authHeaders?.Authorization || '').replace(/Bearer\s+/i, '');
    if (!meetingId || !token) {
      setMeetingRealtimeConnected(false);
      setMeetingPresenceCount(0);
      setMeetingNotes('');
      setMeetingConnectedUsers([]);
      setMeetingFormVisible(false);
      setSelectedMeetingTensionIdShared(null);
      setMeetingPoints([]);
      setMeetingPreparedPoints([]);
      setMeetingPreparedPointsLoading(false);
      setMeetingPreparedPointsError(null);
      setSelectedMeetingPointId(null);
      setSelectedMeetingPointIdShared(null);
      setSelectedMeetingProjectId(null);
      setSelectedMeetingProjectIdShared(null);
      setMeetingProjectReviews([]);
      setMeetingPointComposer(buildEmptyPointComposer());
      if (meetingSocketRef.current) {
        meetingSocketRef.current.close();
        meetingSocketRef.current = null;
      }
      return;
    }
    const wsBase = (apiUrl || '').replace(/^http/i, 'ws');
    const socket = new WebSocket(`${wsBase}/circle-meetings/${meetingId}/ws?token=${encodeURIComponent(token)}`);
    meetingSocketRef.current = socket;
    socket.onopen = () => setMeetingRealtimeConnected(true);
    socket.onclose = () => {
      setMeetingRealtimeConnected(false);
      setMeetingPresenceCount(0);
      setMeetingConnectedUsers([]);
      setMeetingFormVisible(false);
      setSelectedMeetingTensionIdShared(null);
      setMeetingPoints([]);
      setMeetingPreparedPoints([]);
      setMeetingPreparedPointsLoading(false);
      setMeetingPreparedPointsError(null);
      setSelectedMeetingPointId(null);
      setSelectedMeetingPointIdShared(null);
      setSelectedMeetingProjectId(null);
      setSelectedMeetingProjectIdShared(null);
      setMeetingProjectReviews([]);
      setMeetingPointComposer(buildEmptyPointComposer());
      if (meetingSocketRef.current === socket) {
        meetingSocketRef.current = null;
      }
    };
    socket.onerror = () => setMeetingRealtimeConnected(false);
    socket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'sync_state') {
          setMeetingNotes(data.notes?.content || '');
          if (data.tensions) setMeetingTensions((data.tensions || []).map(normalizeTension));
          if (data.participants) setMeetingParticipants(data.participants);
          if (data.meeting) setActiveMeeting(data.meeting);
          if (data.draft !== undefined) setMeetingDraft(normalizeMeetingDraft(data.draft));
          if (typeof data.connected === 'number') setMeetingPresenceCount(data.connected);
          if (Array.isArray(data.connected_users)) setMeetingConnectedUsers(data.connected_users);
          if (typeof data.form_visible === 'boolean') setMeetingFormVisible(data.form_visible);
          if (Array.isArray(data.points)) setMeetingPoints(data.points);
          if (data.meeting_phase !== undefined) {
            setMeetingPhase(data.meeting_phase || 'inclusion');
          }
          if (data.selected_point_id !== undefined) {
            setSelectedMeetingPointIdShared(data.selected_point_id);
            if (data.selected_point_id !== null) {
              setSelectedMeetingPointId(data.selected_point_id);
            }
          }
          if (data.selected_project_id !== undefined) {
            setSelectedMeetingProjectIdShared(data.selected_project_id);
            setSelectedMeetingProjectId(data.selected_project_id || null);
          }
          if (data.point_composer !== undefined) {
            setMeetingPointComposer(normalizePointComposer(data.point_composer, data.selected_project_id || null));
          } else {
            setMeetingPointComposer(buildEmptyPointComposer(data.selected_project_id || null));
          }
          if (Array.isArray(data.project_reviews)) {
            setMeetingProjectReviews((prev) =>
              data.project_reviews.reduce(
                (items, review) => mergeProjectReviewContext(items, review),
                prev
              )
            );
          }
          if (data.secretary_id !== undefined) {
            setActiveMeeting((prev) => (prev ? { ...prev, created_by: data.secretary_id } : prev));
          }
          if (data.selected_tension_id !== undefined) {
            setSelectedMeetingTensionIdShared(data.selected_tension_id);
            if (data.selected_tension_id !== null) {
              setSelectedMeetingTensionId(data.selected_tension_id);
            }
          }
        } else if (data.type === 'note_update') {
          setMeetingNotes(data.content || '');
        } else if (data.type === 'tensions_sync') {
          setMeetingTensions((data.tensions || []).map(normalizeTension));
          if (data.selected_tension_id !== undefined) {
            setSelectedMeetingTensionIdShared(data.selected_tension_id);
            if (data.selected_tension_id !== null) {
              setSelectedMeetingTensionId(data.selected_tension_id);
            }
          }
        } else if (data.type === 'points_sync') {
          setMeetingPoints(Array.isArray(data.points) ? data.points : []);
          if (data.selected_point_id !== undefined) {
            setSelectedMeetingPointIdShared(data.selected_point_id);
            if (data.selected_point_id !== null) {
              setSelectedMeetingPointId(data.selected_point_id);
            }
          }
        } else if (data.type === 'tension_update') {
          setMeetingTensions((prev) =>
            prev.map((t) =>
              String(t.id) === String(data.tension_id)
                ? {
                  ...t,
                  title: data.title !== undefined ? data.title : t.title,
                  summary: data.summary !== undefined ? data.summary : t.summary,
                  action: data.action !== undefined ? data.action : t.action,
                  expressed_by_role_id: data.expressed_by_role_id !== undefined ? data.expressed_by_role_id : t.expressed_by_role_id,
                  proposed_data: { ...(t.proposed_data || {}), ...(data.proposed_data || {}) },
                  operations: data.operations || t.operations || [],
                }
                : t
            )
          );
        } else if (data.type === 'draft_update') {
          setMeetingDraft(normalizeMeetingDraft(data.draft));
        } else if (data.type === 'point_selected') {
          setSelectedMeetingPointIdShared(data.point_id);
          setSelectedMeetingPointId(data.point_id || null);
        } else if (data.type === 'project_selected') {
          setSelectedMeetingProjectIdShared(data.project_id);
          setSelectedMeetingProjectId(data.project_id || null);
        } else if (data.type === 'point_composer') {
          setMeetingPointComposer((prev) => normalizePointComposer(data.composer, prev?.objective_form?.project_id || null));
        } else if (data.type === 'project_review_update') {
          if (data.review) {
            setMeetingProjectReviews((prev) => mergeProjectReviewContext(prev, data.review));
          }
        } else if (data.type === 'presence') {
          setMeetingPresenceCount(typeof data.connected === 'number' ? data.connected : 0);
          if (Array.isArray(data.users)) setMeetingConnectedUsers(data.users);
          if (data.secretary_id !== undefined) {
            setActiveMeeting((prev) => (prev ? { ...prev, created_by: data.secretary_id } : prev));
          }
        } else if (data.type === 'participants_update') {
          if (Array.isArray(data.participants)) setMeetingParticipants(data.participants);
        } else if (data.type === 'secretary_update') {
          setActiveMeeting((prev) => (prev ? { ...prev, created_by: data.secretary_id } : prev));
        } else if (data.type === 'tension_selected') {
          setSelectedMeetingTensionIdShared(data.tension_id);
          setSelectedMeetingTensionId(data.tension_id || null);
        } else if (data.type === 'form_visibility') {
          if (typeof data.visible === 'boolean') setMeetingFormVisible(data.visible);
        } else if (data.type === 'phase_update') {
          if (data.phase !== undefined) setMeetingPhase(data.phase || 'inclusion');
        } else if (data.type === 'meeting_closed') {
          setActiveMeeting(null);
          setMeetingTensions([]);
          setMeetingParticipants([]);
          setMeetingNotes('');
          setMeetingPresenceCount(0);
          setMeetingConnectedUsers([]);
          setMeetingFormVisible(false);
          setMeetingPhase('inclusion');
          setSelectedMeetingTensionIdShared(null);
          setMeetingPoints([]);
          setMeetingPreparedPoints([]);
          setMeetingPreparedPointsLoading(false);
          setMeetingPreparedPointsError(null);
          setSelectedMeetingPointId(null);
          setSelectedMeetingPointIdShared(null);
          setSelectedMeetingProjectId(null);
          setSelectedMeetingProjectIdShared(null);
          setMeetingProjectReviews([]);
          setMeetingPointComposer(buildEmptyPointComposer());
          setSelectedMeetingTensionId(null);
          setMeetingDraft(normalizeMeetingDraft(null));
          setOpenMeetings((prev) => (prev || []).filter((m) => String(m?.id) !== String(data.meeting_id)));
        } else if (data.type === 'error') {
          setMeetingError(data.message || 'Erreur temps reel');
        }
      } catch (err) {
        console.error('WebSocket parse error', err);
      }
    };
    return () => {
      try {
        socket.close();
      } catch (err) {
        // ignore
      }
    };
  }, [activeMeeting?.id, apiUrl, getAuthHeaders, normalizeMeetingDraft, normalizePointComposer, normalizeTension]);

  const handleMeetingDraftChange = useCallback(
    (updater) => {
      setMeetingDraft((prev) => {
        const next = typeof updater === 'function' ? updater(prev) : updater;
        const normalized = normalizeMeetingDraft(next);
        if (String(activeMeeting?.created_by) === String(effectiveUserId)) {
          sendMeetingRealtime({ type: 'draft_update', draft: normalized });
        }
        return normalized;
      });
    },
    [activeMeeting, effectiveUserId, sendMeetingRealtime, normalizeMeetingDraft]
  );

  const handleTransferSecretary = useCallback(
    (nextSecretaryId) => {
      if (!activeMeeting || !nextSecretaryId) return;
      if (String(activeMeeting.created_by) !== String(effectiveUserId)) return;
      const sent = sendMeetingRealtime({ type: 'secretary_transfer', user_id: nextSecretaryId });
      if (!sent) {
        setMeetingError("Transfert du secrétaire impossible (connexion temps reel).");
      }
    },
    [activeMeeting, effectiveUserId, sendMeetingRealtime]
  );

  const handleMeetingNotesChange = useCallback((content) => {
    setMeetingNotes(content);
    if (meetingNoteDebounce.current) clearTimeout(meetingNoteDebounce.current);
    meetingNoteDebounce.current = setTimeout(() => {
      sendMeetingRealtime({ type: 'note_update', content });
    }, 300);
  }, [sendMeetingRealtime]);

  const fetchMeetingProjectReviews = useCallback(
    async (projectIds = []) => {
      if (!activeMeeting?.id) {
        setMeetingProjectReviews([]);
        return [];
      }
      const normalizedIds = Array.isArray(projectIds)
        ? projectIds.filter((id) => id !== null && id !== undefined && id !== '')
        : [];
      const query = normalizedIds.length > 0
        ? `?project_ids=${encodeURIComponent(normalizedIds.join(','))}`
        : '';
      try {
        const response = await fetch(`${apiUrl}/circle-meetings/${activeMeeting.id}/project-reviews${query}`, {
          headers: getAuthHeaders(),
        });
        if (!response.ok) throw new Error("Impossible de recuperer les revues de projets");
        const data = await response.json();
        const normalized = (Array.isArray(data) ? data : [])
          .map(normalizeProjectReviewContext)
          .filter(Boolean);
        setMeetingProjectReviews(normalized);
        return normalized;
      } catch (err) {
        setMeetingError(err.message);
        return [];
      }
    },
    [activeMeeting?.id, apiUrl, getAuthHeaders]
  );

  const handleUpdateMeetingProjectReview = useCallback(
    async (projectId, review) => {
      if (!activeMeeting?.id || !projectId) return null;
      if (String(activeMeeting.created_by) !== String(effectiveUserId)) return null;
      const payload = {
        ...(review || {}),
        project_id: Number(projectId),
      };
      const sent = sendMeetingRealtime({
        type: 'project_review_update',
        project_id: Number(projectId),
        review: payload,
      });
      if (sent) return payload;
      try {
        const response = await fetch(`${apiUrl}/circle-meetings/${activeMeeting.id}/project-reviews/${projectId}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify(payload),
        });
        if (!response.ok) throw new Error("Mise a jour de la revue du projet impossible");
        const updated = await response.json();
        setMeetingProjectReviews((prev) => mergeProjectReviewContext(prev, updated));
        return updated;
      } catch (err) {
        setMeetingError(err.message);
        return null;
      }
    },
    [activeMeeting?.id, activeMeeting?.created_by, effectiveUserId, apiUrl, getAuthHeaders, sendMeetingRealtime]
  );

  const handleOpenMeeting = async (type = 'gouvernance', circleOverride = selectedCircle) => {
    if (!circleOverride?.id) return;
    try {
      setMeetingLoading(true);
      const response = await fetch(`${apiUrl}/circle-meetings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ circle_id: circleOverride.id, type }),
      });
      if (!response.ok) throw new Error("Création de réunion impossible");
      const meeting = await response.json();
      setActiveMeeting(meeting);
      setSelectedCircle(circleOverride);
      setActiveTab('meeting');
      setMeetingNotes('');
      setMeetingPresenceCount(0);
      fetchMeetingTensions(meeting.id);
      fetchMeetingParticipants(meeting.id);
      fetchOpenMeetingsForUser();
    } catch (err) {
      setMeetingError(err.message);
    } finally {
      setMeetingLoading(false);
    }
  };

  const handleCloseMeeting = async () => {
    if (!activeMeeting) return;
    try {
      setMeetingLoading(true);
      const response = await fetch(`${apiUrl}/circle-meetings/${activeMeeting.id}/close`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error("Clôture impossible");
      setActiveMeeting(null);
      setMeetingTensions([]);
      setMeetingParticipants([]);
      setMeetingNotes('');
      setMeetingPresenceCount(0);
      setMeetingPoints([]);
      setMeetingPreparedPoints([]);
      setMeetingPreparedPointsLoading(false);
      setMeetingPreparedPointsError(null);
      setSelectedMeetingPointId(null);
      setSelectedMeetingPointIdShared(null);
      setSelectedMeetingProjectId(null);
      setSelectedMeetingProjectIdShared(null);
      setMeetingProjectReviews([]);
      setMeetingPointComposer(buildEmptyPointComposer());
      fetchOpenMeetingsForUser();
    } catch (err) {
      setMeetingError(err.message);
    } finally {
      setMeetingLoading(false);
    }
  };

  const handleOpenMeetingHistory = useCallback(
    async (circleOverride = selectedCircle) => {
      if (!circleOverride?.id) return;
      try {
        setMeetingHistoryLoading(true);
        setMeetingHistoryError(null);
        setShowMeetingHistory(true);
        const response = await fetch(`${apiUrl}/circle-meetings/history?circle_id=${circleOverride.id}`, {
          headers: getAuthHeaders(),
        });
        if (!response.ok) throw new Error("Impossible de recuperer l'historique");
        const data = await response.json();
        setMeetingHistory(Array.isArray(data) ? data : []);
      } catch (err) {
        setMeetingHistoryError(err.message);
      } finally {
        setMeetingHistoryLoading(false);
      }
    },
    [apiUrl, getAuthHeaders, selectedCircle]
  );

  const handleCloseMeetingHistory = useCallback(() => {
    setShowMeetingHistory(false);
  }, []);

  const handleImportMeetingTension = async (tensionId) => {
    if (!activeMeeting || !tensionId) return;
    if (sendMeetingRealtime({ type: 'tension_import', tension_id: tensionId })) return;
    try {
      const response = await fetch(`${apiUrl}/circle-meetings/${activeMeeting.id}/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ tension_id: tensionId }),
      });
      if (!response.ok) throw new Error("Import impossible");
      await fetchMeetingTensions(activeMeeting.id);
    } catch (err) {
      setMeetingError(err.message);
    }
  };

  const handleCreateMeetingTension = async () => {
    if (!activeMeeting) return;
    const draft = normalizeMeetingDraft(meetingDraft);
    const ops = Array.isArray(draft.operations) ? draft.operations : [];
    if (ops.length === 0) {
      setMeetingError("Aucune operation définie");
      return;
    }
    const firstOp = ops[0] || {};
    const action = firstOp.action || draft.action || 'edit';
    const targetCircle = (circles || []).find((circle) =>
      String(circle.id) === String(firstOp.target_circle_id || firstOp.circle_id || draft.circle_id)
    );
    const title = draft.title?.trim()
      || firstOp.data?.name
      || targetCircle?.name
      || 'Tension en reunion';
    const payload = {
      type: 'tension_add',
      title,
      summary: draft.summary,
      action,
      circle_id: draft.circle_id,
      expressed_by_role_id: draft.expressed_by_role_id ? Number(draft.expressed_by_role_id) : null,
      operations: ops,
    };
    const sent = sendMeetingRealtime(payload);
    if (sent) {
      handleMeetingDraftChange(normalizeMeetingDraft(null));
      return;
    }
    try {
      const response = await fetch(`${apiUrl}/circle-meetings/${activeMeeting.id}/tensions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error("Création impossible");
      handleMeetingDraftChange(normalizeMeetingDraft(null));
      await fetchMeetingTensions(activeMeeting.id);
    } catch (err) {
      setMeetingError(err.message);
    }
  };

  const handleJoinMeeting = useCallback(
    (circleOrMeeting) => {
      if (!circleOrMeeting) return;
      const meeting = circleOrMeeting.circle_id
        ? circleOrMeeting
        : openMeetingsByCircle.get(String(circleOrMeeting.id));
      const circle = circleOrMeeting.circle_id
        ? circles.find((c) => String(c.id) === String(circleOrMeeting.circle_id))
        : circleOrMeeting;
      if (!meeting || !circle) return;
      setSelectedCircle(circle);
      setActiveTab('meeting');
      setMeetingError(null);
      if (activeMeeting?.id && String(activeMeeting.id) === String(meeting.id)) {
        meetingRejoinPending.current = true;
        setActiveMeeting(null);
        setTimeout(() => {
          setActiveMeeting(meeting);
          meetingRejoinPending.current = false;
        }, 0);
      } else {
        meetingRejoinPending.current = false;
        setActiveMeeting(meeting);
      }
    },
    [openMeetingsByCircle, circles, activeMeeting]
  );

  const handleToggleMeetingForm = (visible) => {
    const next = typeof visible === 'boolean' ? visible : !meetingFormVisible;
    setMeetingFormVisible(next);
    sendMeetingRealtime({ type: 'form_toggle', visible: next });
  };

  const handleAddMeetingPoint = useCallback(
    (title) => {
      const trimmed = (title || '').trim();
      if (!trimmed) return;
      if (!sendMeetingRealtime({ type: 'point_add', title: trimmed })) {
        setMeetingError("Ajout du point impossible (connexion temps reel).");
      }
    },
    [sendMeetingRealtime]
  );

  const handleImportMeetingPoint = useCallback(
    (pointId) => {
      if (!activeMeeting || !pointId) return;
      if (!sendMeetingRealtime({ type: 'point_import', point_id: pointId })) {
        setMeetingError("Import du point impossible (connexion temps reel).");
      }
    },
    [activeMeeting, sendMeetingRealtime]
  );

  const handleSelectMeetingPoint = useCallback(
    (pointId) => {
      if (!activeMeeting) return;
      if (String(activeMeeting.created_by) !== String(effectiveUserId)) return;
      setSelectedMeetingPointId(pointId);
      setMeetingPointComposer(buildEmptyPointComposer(selectedMeetingProjectId));
      sendMeetingRealtime({ type: 'point_select', point_id: pointId });
    },
    [activeMeeting, effectiveUserId, sendMeetingRealtime, selectedMeetingProjectId]
  );

  const handleMeetingPointDecision = useCallback(
    (pointId, status) => {
      if (!activeMeeting) return;
      if (String(activeMeeting.created_by) !== String(effectiveUserId)) return;
      if (!sendMeetingRealtime({ type: 'point_decision', point_id: pointId, status })) {
        setMeetingError("Mise a jour du point impossible (connexion temps reel).");
      }
    },
    [activeMeeting, effectiveUserId, sendMeetingRealtime]
  );

  const handleMeetingPointToTension = useCallback(
    (point) => {
      if (!activeMeeting || !point) return;
      if (String(activeMeeting.created_by) !== String(effectiveUserId)) return;
      if (!sendMeetingRealtime({ type: 'point_to_tension', point_id: point.id })) {
        setMeetingError("Conversion du point impossible (connexion temps reel).");
        return;
      }
      const base = normalizeMeetingDraft(null);
      handleMeetingDraftChange({ ...base, title: point.title || '' });
      handleToggleMeetingForm(true);
    },
    [activeMeeting, effectiveUserId, sendMeetingRealtime, normalizeMeetingDraft, handleMeetingDraftChange, handleToggleMeetingForm]
  );

  const handleMeetingPointNotesChange = useCallback(
    (pointId, notes) => {
      if (!activeMeeting || !pointId) return;
      if (meetingPointNoteDebounce.current) clearTimeout(meetingPointNoteDebounce.current);
      meetingPointNoteDebounce.current = setTimeout(() => {
        if (!sendMeetingRealtime({ type: 'point_note', point_id: pointId, notes: notes || '' })) {
          setMeetingError("Mise a jour des notes impossible (connexion temps reel).");
        }
      }, 300);
    },
    [activeMeeting, sendMeetingRealtime]
  );

  const handleMeetingPointComposerUpdate = useCallback(
    (nextComposer) => {
      if (!activeMeeting) return false;
      if (String(activeMeeting.created_by) !== String(effectiveUserId)) return false;
      const normalized = normalizePointComposer(nextComposer, selectedMeetingProjectId);
      setMeetingPointComposer(normalized);
      if (!sendMeetingRealtime({ type: 'point_composer_update', composer: normalized })) {
        setMeetingError("Synchronisation du formulaire impossible (connexion temps reel).");
        return false;
      }
      return true;
    },
    [activeMeeting, effectiveUserId, normalizePointComposer, selectedMeetingProjectId, sendMeetingRealtime]
  );

  const handleSelectMeetingProject = useCallback(
    (projectId) => {
      if (!activeMeeting) return;
      if (String(activeMeeting.created_by) !== String(effectiveUserId)) return;
      setSelectedMeetingProjectId(projectId);
      sendMeetingRealtime({ type: 'project_select', project_id: projectId });
    },
    [activeMeeting, effectiveUserId, sendMeetingRealtime]
  );

  const handleSelectMeetingPhase = useCallback(
    (phase) => {
      if (!activeMeeting) return;
      if (String(activeMeeting.created_by) !== String(effectiveUserId)) return;
      const nextPhase = phase || 'inclusion';
      setMeetingPhase(nextPhase);
      sendMeetingRealtime({ type: 'phase_select', phase: nextPhase });
    },
    [activeMeeting, effectiveUserId, sendMeetingRealtime]
  );

  const handleMeetingPointAction = useCallback(
    (pointId, action) => {
      if (!activeMeeting || !pointId || !action) return;
      if (String(activeMeeting.created_by) !== String(effectiveUserId)) return;
      if (!sendMeetingRealtime({ type: 'point_action', point_id: pointId, action })) {
        setMeetingError("Ajout d'action impossible (connexion temps reel).");
      }
    },
    [activeMeeting, effectiveUserId, sendMeetingRealtime]
  );

  const handleInviteMeetingParticipant = useCallback(
    async (employeeId) => {
      if (!activeMeeting?.id || !employeeId) return;
      try {
        const response = await fetch(`${apiUrl}/circle-meetings/${activeMeeting.id}/invites`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify({ employee_id: employeeId }),
        });
        if (!response.ok) throw new Error("Invitation impossible");
        const data = await response.json();
        if (Array.isArray(data?.participants)) {
          setMeetingParticipants(data.participants);
        } else {
          fetchMeetingParticipants(activeMeeting.id);
        }
      } catch (err) {
        setMeetingError(err.message);
      }
    },
    [activeMeeting?.id, apiUrl, getAuthHeaders, fetchMeetingParticipants]
  );

  const handleCreateTacticalObjective = useCallback(
    async (pointId, payload) => {
      if (!payload?.project_id || !payload?.name) return null;
      try {
        const effortValue = Number(payload.effort);
        const normalizedEffort = Number.isFinite(effortValue) && effortValue >= 0
          ? Math.floor(effortValue)
          : 1;
        const response = await fetch(`${apiUrl}/projects/${payload.project_id}/objectives`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify({
            project_id: payload.project_id,
            name: payload.name,
            owner_id: payload.owner_id || null,
            sprint_id: payload.sprint_id || null,
            effort: normalizedEffort,
            description: payload.description || null,
          }),
        });
        if (!response.ok) throw new Error("Création d'objectif impossible");
        const created = await response.json();
        handleMeetingPointAction(pointId, {
          type: 'objective',
          objective_id: created.id,
          name: created.name,
          project_id: created.project_id,
          owner_id: created.owner_id || null,
        });
        return created;
      } catch (err) {
        setMeetingError(err.message);
        return null;
      }
    },
    [apiUrl, getAuthHeaders, handleMeetingPointAction]
  );

  const handleCreateTacticalTask = useCallback(
    async (pointId, payload) => {
      if (!payload?.name || !payload?.owner_id) return null;
      try {
        const response = await fetch(`${apiUrl}/tasks`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify({
            name: payload.name,
            owner_id: payload.owner_id,
            project_id: payload.project_id || null,
          }),
        });
        if (!response.ok) throw new Error("Création de t?che impossible");
        const created = await response.json();
        handleMeetingPointAction(pointId, {
          type: 'task',
          task_id: created.id,
          name: created.name,
          owner_id: created.owner_id || null,
          project_id: created.project_id || null,
        });
        return created;
      } catch (err) {
        setMeetingError(err.message);
        return null;
      }
    },
    [apiUrl, getAuthHeaders, handleMeetingPointAction]
  );

  const handleLiveMeetingTensionEdit = (tensionId, updates) => {
    if (!activeMeeting || !tensionId) return;
    if (String(activeMeeting.created_by) !== String(effectiveUserId)) return;

    // Optimistic update local state IMMEDIATELY (single source of truth)
    setMeetingTensions((prev) =>
      prev.map((t) => (t.id === tensionId ? { ...t, ...updates, proposed_data: { ...(t.proposed_data || {}), ...(updates.proposed_data || {}) } } : t))
    );

    // Prepare payload
    const payload = {
      type: 'tension_update',
      tension_id: tensionId,
      title: updates.title,
      summary: updates.summary,
      action: updates.action,
      proposed_data: updates.proposed_data,
      operations: updates.operations || [],
      expressed_by_role_id: updates.expressed_by_role_id,
    };

    // Store pending payload for flush
    pendingTensionUpdate.current = payload;

    // Debounce network sync
    if (tensionEditDebounce.current) clearTimeout(tensionEditDebounce.current);

    tensionEditDebounce.current = setTimeout(() => {
      if (sendMeetingRealtime(payload)) {
        pendingTensionUpdate.current = null; // Clear pending on success
      } else {
        // Fallback: recharger depuis l'API si websocket absent
        if (activeMeeting?.id) fetchMeetingTensions(activeMeeting.id);
      }
    }, 250);
  };

  const handleMeetingDecision = async (tensionId, decision) => {
    if (!activeMeeting) return;
    if (String(activeMeeting.created_by) !== String(effectiveUserId)) {
      setMeetingError("Seul le secrétaire peut valider");
      return;
    }
    if (pendingTensionUpdate.current) {
      sendMeetingRealtime(pendingTensionUpdate.current);
      pendingTensionUpdate.current = null;
    }
    if (sendMeetingRealtime({ type: 'decision', tension_id: tensionId, decision })) return;
    try {
      const response = await fetch(`${apiUrl}/circle-meetings/${activeMeeting.id}/tensions/${tensionId}/decision?decision=${decision}`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error("Decision impossible");
      await fetchMeetingTensions(activeMeeting.id);
    } catch (err) {
      setMeetingError(err.message);
    }
  };

  return {
    activeMeeting,
    meetingTensions,
    meetingParticipants,
    meetingNotes,
    meetingPresenceCount,
    meetingConnectedUsers,
    meetingRealtimeConnected,
    meetingFormVisible,
    meetingPhase,
    meetingPoints,
    meetingPreparedPoints,
    meetingPreparedPointsLoading,
    meetingPreparedPointsError,
    selectedMeetingPointId,
    selectedMeetingPointIdShared,
    selectedMeetingProjectId,
    selectedMeetingProjectIdShared,
    meetingProjectReviews,
    meetingPointComposer,
    meetingLoading,
    meetingError,
    meetingHistory,
    meetingHistoryLoading,
    meetingHistoryError,
    showMeetingHistory,
    meetingDraft,
    selectedMeetingTensionId,
    selectedMeetingTensionIdShared,
    openMeetings,
    openMeetingsByCircle,
    openMeetingCircleIds,
    setSelectedMeetingTensionId,
    setSelectedMeetingPointId,
    setActiveMeeting,
    fetchMeetingTensions,
    fetchMeetingPreparedPoints,
    fetchMeetingProjectReviews,
    fetchOpenMeetingsForUser,
    sendMeetingRealtime,
    handleMeetingDraftChange,
    handleTransferSecretary,
    handleMeetingNotesChange,
    handleOpenMeeting,
    handleCloseMeeting,
    handleOpenMeetingHistory,
    handleCloseMeetingHistory,
    handleImportMeetingTension,
    handleCreateMeetingTension,
    handleJoinMeeting,
    handleToggleMeetingForm,
    handleAddMeetingPoint,
    handleImportMeetingPoint,
    handleSelectMeetingPoint,
    handleMeetingPointDecision,
    handleMeetingPointToTension,
    handleMeetingPointNotesChange,
    handleMeetingPointComposerUpdate,
    handleSelectMeetingProject,
    handleSelectMeetingPhase,
    handleUpdateMeetingProjectReview,
    handleCreateTacticalTask,
    handleCreateTacticalObjective,
    handleInviteMeetingParticipant,
    handleLiveMeetingTensionEdit,
    handleMeetingDecision,
  };
};

export default useMeetingState;
